<template>
<v-dialog v-model="dialog" max-width="800px">
	<v-card class="elevation-12">
		<v-toolbar
			color="primary"
			dark
			flat>
			<v-toolbar-title>User Account</v-toolbar-title>
		</v-toolbar>
		<v-card-text>
			<preloader text="Loading" v-if="loading" />
			<v-form
				ref="form"
				v-show="!loading"
				v-model="formValid"
				lazy-validation>
				<v-row>
					<v-col cols="12" md="4">
						<v-text-field
							autofocus
							label="Firstname"
							prepend-icon="mdi-account"
							v-model="item.firstname"
							:rules="[rules.required]"/>
					</v-col>
					<v-col cols="12" md="4">
						<v-text-field 
							v-model="item.middlename" 
							prepend-icon="mdi-none"
							label="Middlename"/>
					</v-col>
					<v-col cols="12" md="4">
						<v-text-field 
							v-model="item.lastname" 
							prepend-icon="mdi-none"
							label="Lastname"
							:rules="[rules.required]"/>
					</v-col>
				</v-row>

				<v-row>
					<v-col cols="12" md="7">
						<v-text-field 
							v-model="item.title" 
							prepend-icon="mdi-book"
							hint="Ex. Trainer, Registrar Staff"
							label="Title"/>
					</v-col>
					<v-col cols="12" md="5">
						<v-text-field 
							v-model="item.email" 
							prepend-icon="mdi-email"
							label="Email"
							:rules="[rules.email]"/>
					</v-col>
				</v-row>

				<v-row>
					<v-col cols="12" md="4">
						<v-text-field
							v-model="item.username"
							prepend-icon="mdi-account"
							:rules="[rules.required]"
							label="Username"
						></v-text-field>
					</v-col>

					<v-col cols="12" md="4">
						<v-text-field
							v-model="item.password"
							prepend-icon="mdi-lock"
							:append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
							:rules="!(item.id)? [rules.required, rules.min] : []"
							:type="showPass ? 'text' : 'password'"
							label="Password"
							hint="Leave blank if no changes"
							@click:append="showPass = !showPass"
						></v-text-field>
					</v-col>

					<v-col cols="12" md="4">
						<v-select
							v-model="item.type"
							:items="userTypes"
							item-text="text"
							item-value="value"
							:readonly="selfUpdateMode"
							label="Type"
							:rules="[rules.required]">
						</v-select>
					</v-col>
				</v-row>

			</v-form>
		</v-card-text>
		<v-card-actions>
			<v-spacer />
			<v-btn 
				@click="close">
				Close
			</v-btn>
			<v-btn 
				color="primary"
				@click="save"
				:disabled="loading">
				Save
			</v-btn>
		</v-card-actions>
		</v-card>
	
</v-dialog> 
</template>

<script>
import Api from '../../services/api'
import Notify  from '../../services/notify'
import { USER } from '../../services/constants'
import FormRules from '../../mixins/form-rules.mixin'
import Preloader from '../main/Preloader'

export default {
	name: 'userForm',
	components: { Preloader },
	mixins: [ FormRules ],
	data() {
		return {
			dialog: false,
			loading: false,
			formValid: true,
			existingUsername: null,
			showPass: true,
			selfUpdateMode: false,
			userTypes: [
				{ text: 'Admin', value: USER.ADMIN },
				{ text: 'Teacher', value: USER.TEACHER },
				{ text: 'Registrar', value: USER.REGISTRAR },
				{ text: 'Training Dept.', value: USER.TRAINING },
				{ text: 'IT Support', value: USER.IT},
			],
			item: { },
			defaultItem: {
				firstname: null,
				middlename: '',
				lastname: null,
				title: null,
				email: '',
				username: null,
				password: null,
				type: USER.TEACHER
			},
		}
	},
	methods: {
		close () {
			this.selfUpdateMode = false;
			this.dialog = false;
			this.resetFields();
		},

		createUser() {
			this.dialog = true;
			this.resetFields();
		},

		resetFields() {
			this.item = Object.assign({}, this.defaultItem);
		},

		updateUser (userId, selfUpdateMode) {
			if(selfUpdateMode) {
				this.selfUpdateMode = true;
			}
			this.dialog = true;
			this.loading = true;
			let queryObj = Api.user.get;
			if(selfUpdateMode) {
				queryObj = Api.user.getSelf;
			}

			queryObj(userId).then((response) => {
					this.item = Object.assign({}, response.data);
				})
				.catch(() => { Notify.error.oops() })
				.finally(() => { this.loading = false });
		},

		deleteUser(userId) {
			Notify.confirm('Delete this user?', "Action can't be undone.")
				.then(() => {
					Api.user.delete(userId)
						.then(() => {
							Notify.success.itemDeleted();
							this.$emit('user-deleted');
								this.close();
						})
						.catch(() => { Notify.error.oops() });
				})
				.catch(() => {});
		},

		save () {
			if(!this.$refs.form.validate()) {
				return;
			}

			if(!this.item.id) { // create
				this.loading = true;
				this.checkUsername(this.item)
					.then(() => {
						this.loading = true;
						Api.user.create(this.item)
							.then(() => {
								Notify.success.userCreated();
								this.$emit('user-created');
								this.close();
							})
							.finally(() => { this.loading = false });
					})
					.finally(this.loading = false); 
			} 
			else if(this.item.id) { // update
				this.loading = true;
				this.checkUsername(this.item)
					.then(() => {
						this.loading = true;

						let queryObj = Api.user.update;
						if(this.selfUpdateMode) {
							queryObj = Api.user.updateSelf;
						}
						
						queryObj(this.item)
							.then(() => {
								Notify.success.userUpdated();
								this.close();
								this.$emit('user-updated');
							})
							.catch(() => { Notify.error.noChanges() })
							.finally(() => { this.loading = false; });
					})
					.finally(() => { this.loading = true });
			}
		},

		checkUsername(item) {
			return new Promise((resolve) => {
				Api.user.check(item)
					.then(() => {
						resolve();
					})
					.catch(() => {	Notify.error.userExists() });
			});
		}
	}
}
</script>